import type { ConsentCookie } from '../types/usercentrics';
import { defaultConsent } from '../types/usercentrics';

// Cookie utility functions to replace Nuxt composables
export const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

export const setCookie = (name: string, value: string, options: {
  maxAge?: number;
  path?: string;
} = {}) => {
  let cookieString = `${name}=${value}`;

  if (options.maxAge) {
    cookieString += `; max-age=${options.maxAge}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  document.cookie = cookieString;
};

// Reactive cookie implementation
export const useCookie = <T>(name: string, options: {
  maxAge?: number;
  path?: string;
  default?: () => T;
} = {}) => {
  const getValue = (): T => {
    const cookieValue = getCookie(name);
    if (cookieValue) {
      try {
        return JSON.parse(cookieValue);
      } catch {
        return cookieValue as T;
      }
    }
    return options.default ? options.default() : undefined as T;
  };

  const setValue = (newValue: T) => {
    const stringValue = typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
    setCookie(name, stringValue, options);
  };

  return {
    get value() {
      return getValue();
    },
    set value(newValue: T) {
      setValue(newValue);
    }
  };
};

// Specific consent cookie implementation
const maxAge = 60 * 60 * 24 * 365; // 1 year

export const useConsentCookie = () => {
  return useCookie<ConsentCookie>('consent', {
    maxAge,
    path: '/',
    default: () => ({ ...defaultConsent }),
  });
};
